// Wording for the attempt entry form and its declaration preview.
// CommonJS, no destructuring (a Babel helper import would turn this file into
// an ES module in the production build and break named imports).

// "What did you observe?" choices. Stored as plain text in the existing
// service_attempts.address_observations column — no schema change.
const OBSERVATION_OPTIONS_BASE = [
  "Nothing notable",
  "No one home — no lights or movement inside",
  "No vehicles present",
  "Gate or building locked — no access",
  "Property appears vacant",
  "Mail addressed to subject was present",
  "Mail addressed to someone else was present",
];
const BUSINESS_CLOSED = "Business was closed";
const OBSERVATION_OTHER = "Other (type it in)";

// A business location gets "Business was closed" at the top of the list.
function observationOptions(locationType) {
  const list = OBSERVATION_OPTIONS_BASE.slice();
  if (String(locationType || "").toLowerCase() === "business") list.splice(1, 0, BUSINESS_CLOSED);
  list.push(OBSERVATION_OTHER);
  return list;
}

// The observation text to save: the typed text when "Other" was picked.
function observationValue(observed, observedOther) {
  if (observed === OBSERVATION_OTHER) {
    const t = String(observedOther || "").trim();
    return t || null;
  }
  return observed ? String(observed) : null;
}

const isNobody = (personPresent) => String(personPresent || "").trim().toLowerCase() === "nobody";

// "a residence" / "an apartment" — lower case with the right article.
function withArticle(word) {
  const w = String(word || "").trim().toLowerCase();
  if (!w) return "";
  return (/^[aeiou]/.test(w) ? "an " : "a ") + w;
}

// The sentence for an attempt where no one was available, with the reason.
function nobodySentence(locationType, observed) {
  const o = String(observed || "").trim();
  const business = String(locationType || "").toLowerCase() === "business";
  if (o === BUSINESS_CLOSED) return "No one was available to accept service because the business was closed.";
  if (o && o !== "Nothing notable") {
    return `No one was available to accept service. Reason: ${o.charAt(0).toLowerCase()}${o.slice(1).replace(/\.$/, "")}.`;
  }
  return business
    ? "No one was available at the business to accept service."
    : "No one answered the door and no one was available to accept service.";
}

// BUSINESS SERVICE. At a business the question is whether the person who took
// the papers is authorized to accept them. Only an authorized person can be
// served: "No" is not a service, so the served entry starts over.
const BUSINESS_AUTHORIZED = "Authorized to accept service";
const BUSINESS_NOT_AUTHORIZED = "No";
const BUSINESS_NOT_AUTHORIZED_MSG = "This person is not authorized to accept service, so it can't be recorded as served. Serve an authorized person, or pick an attempt outcome instead.";

const isBusiness = (locationType) => String(locationType || "").trim().toLowerCase() === "business";

// The form fields the answer implies. "No" clears the served entry.
function businessAuthorityFields(choice) {
  if (choice === BUSINESS_AUTHORIZED) return { serviceAccepted: BUSINESS_AUTHORIZED, serviceType: "personal", outcome: "served_personal", status: "served", personPresent: "other" };
  if (choice === BUSINESS_NOT_AUTHORIZED) return { serviceAccepted: "", serviceType: "", outcome: "", status: "attempted", personPresent: "", personPresentDetail: "", relationship: "" };
  return {};
}

// What is still missing before a business service can be saved.
function businessServiceMissing(form) {
  const f = form || {};
  const out = [];
  if (f.serviceAccepted !== BUSINESS_AUTHORIZED) out.push("that the person is authorized to accept service");
  if (!String(f.personPresentDetail || "").trim()) out.push("the name of the person served");
  return out;
}

function businessServedSentence(name, title) {
  const n = String(name || "").trim() || "___";
  const t = String(title || "").trim();
  return `I served ${t ? `${n}, ${t},` : n} who is authorized to accept service.`;
}

// The saved attempt told as a sentence or two — what the admin reads when she
// opens an attempt later. Built only from what was recorded.
const PRESENT_LABELS = {
  subject: "the person to be served", spouse: "the spouse", family_member: "a family member",
  roommate: "a roommate / resident", neighbor: "a neighbor", property_manager: "the property manager",
  security: "security / the gate attendant", coworker: "an employer / co-worker",
};
function presentLabel(code, detail) {
  const c = String(code || "").trim().toLowerCase();
  if (!c) return "";
  if (c === "nobody") return "nobody";
  if (c === "other") return String(detail || "").trim() || "someone (other)";
  return PRESENT_LABELS[c] || c.replace(/_/g, " ");
}
function attemptNarrative(row) {
  const r = row || {};
  const served = /^served_/.test(String(r.outcome || ""));
  const where = [String(r.address_line || r.service_address || "").trim(), r.location_type ? withArticle(r.location_type) : ""].filter(Boolean).join(", ");
  const when = [r.attempt_date, r.attempt_time].filter(Boolean).join(" at ");
  const parts = [];
  const verb = served ? "Served" : "Attempted service";
  parts.push(`${verb}${when ? ` on ${when}` : ""}${where ? ` at ${where}` : ""}.`);
  const code = String(r.person_contacted_category || "").toLowerCase();
  if (isBusiness(r.location_type) && served) {
    parts.push(businessServedSentence(r.person_contacted_detail, r.relationship));
  } else if (code === "nobody") {
    parts.push(nobodySentence(r.location_type, r.address_observations));
  } else if (served && code === "subject") {
    parts.push(r.outcome === "served_substitute" ? "Papers left for the person to be served." : "Personally served the person named in the papers.");
  } else if (code) {
    const who = presentLabel(code, r.person_contacted_detail);
    parts.push(`${served ? "Served" : "Spoke with"} ${who}${r.person_contacted_detail && code !== "other" ? ` (${r.person_contacted_detail})` : ""}${r.relationship ? `, ${r.relationship}` : ""}.`);
    if (r.address_observations && r.address_observations !== "Nothing notable") parts.push(`Observed: ${r.address_observations}.`);
  } else if (r.address_observations && r.address_observations !== "Nothing notable") {
    parts.push(`Observed: ${r.address_observations}.`);
  }
  if (r.statement_made) parts.push(`They said: "${String(r.statement_made).trim()}".`);
  if (r.internal_notes) parts.push(`Notes: ${String(r.internal_notes).trim()}`);
  return parts.join(" ");
}

module.exports = {
  presentLabel, attemptNarrative,
  BUSINESS_AUTHORIZED, BUSINESS_NOT_AUTHORIZED, BUSINESS_NOT_AUTHORIZED_MSG,
  isBusiness, businessAuthorityFields, businessServiceMissing, businessServedSentence,
  OBSERVATION_OPTIONS_BASE, BUSINESS_CLOSED, OBSERVATION_OTHER,
  observationOptions, observationValue, isNobody, withArticle, nobodySentence,
};
